import { useCallback, useEffect, useRef, useState } from 'react'

import { getSidebarCookie, setSidebarCookie } from '@/utils'
import {
	effectiveWidthPx,
	KEYBOARD_STEP,
	MIN_EXPANDED_WIDTH,
	nextStateFromDrag,
	parseSidebarCookie,
	serializeSidebarCookie
} from '@/utils/sidebarLayout.utils'

// Por debajo de este desplazamiento (px) un gesto sobre el grip cuenta como CLICK (toggle),
// no como arrastre — el umbral clásico para no confundir un click con pulso tembloroso.
const CLICK_SLOP_PX = 4

/**
 * Estado vivo del menú lateral (colapsado + ancho) con persistencia en la cookie compartida
 * `sidebar_vibia` (espejo de professional-area/src/components/sidebar/use-sidebar-layout.ts;
 * ver el porqué de la copia en sidebarLayout.utils.js). El drag replica el patrón del SidePanel
 * del UIKit nuevo: pointer capture — sobrevive a los iframes —, listeners en `window` y
 * persistencia solo al soltar. El grip es a la vez toggle y resizer: un gesto que apenas se
 * mueve es un click → pliega/despliega.
 *
 * A diferencia de Next (que lee la cookie en servidor), aquí es SPA pura: la cookie se lee en el
 * primer render, antes del primer paint del shell.
 *
 * Además publica el ancho efectivo como custom property `--vib-eproject-sidebar-w` en <body>:
 * la consume el grid del shell y también el footer fijo (FixedBottom), que es HERMANO del shell
 * y no puede heredar nada de él. En la banda de rail (768–1199px) el CSS fuerza el rail por
 * media query, ignorando la variable — sin sincronización JS con el viewport a propósito.
 */
const useSidebarLayout = () => {
	const [layout, setLayout] = useState(() => parseSidebarCookie(getSidebarCookie()))
	const [resizing, setResizing] = useState(false)
	const sidebarRef = useRef(null)
	// Los handlers de drag (registrados una vez por gesto) leen siempre el estado actual de aquí.
	const layoutRef = useRef(layout)
	layoutRef.current = layout

	useEffect(() => {
		document.body.style.setProperty('--vib-eproject-sidebar-w', `${effectiveWidthPx(layout)}px`)
		return () => document.body.style.removeProperty('--vib-eproject-sidebar-w')
	}, [layout])

	// El footer fijo (hermano del shell) también transiciona con la variable: durante el drag
	// se marca <body> para apagarle la transición y que siga el puntero 1:1, como el grid.
	useEffect(() => {
		document.body.classList.toggle('vib-eproject-sidebar-resizing', resizing)
		return () => document.body.classList.remove('vib-eproject-sidebar-resizing')
	}, [resizing])

	const apply = useCallback((next) => {
		setLayout(next)
		setSidebarCookie(serializeSidebarCookie(next))
	}, [])

	const toggle = useCallback(() => {
		const current = layoutRef.current
		apply({ ...current, collapsed: !current.collapsed })
	}, [apply])

	const onGripPointerDown = useCallback((event) => {
		const aside = sidebarRef.current
		if (!aside || event.button !== 0) return
		event.preventDefault()
		const startWidth = aside.getBoundingClientRect().width
		const startX = event.clientX
		let dragging = false
		setResizing(true)
		// Pointer capture: los moves siguen llegando aunque el cursor cruce un iframe
		// (los iframes se tragan los pointer events no capturados).
		try { event.currentTarget.setPointerCapture(event.pointerId) } catch (e) { /* ignore */ }
		const move = (ev) => {
			dragging = dragging || Math.abs(ev.clientX - startX) >= CLICK_SLOP_PX
			if (dragging) setLayout(nextStateFromDrag(startWidth + (ev.clientX - startX), layoutRef.current))
		}
		const up = () => {
			window.removeEventListener('pointermove', move)
			window.removeEventListener('pointerup', up)
			setResizing(false)
			// Persistencia solo al soltar: una escritura de cookie por gesto, no por pixel.
			if (dragging) apply(layoutRef.current)
			else toggle()
		}
		window.addEventListener('pointermove', move)
		window.addEventListener('pointerup', up)
	}, [apply, toggle])

	const onGripKeyDown = useCallback((event) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			toggle()
			return
		}
		const current = layoutRef.current
		const base = current.collapsed || current.width == null
			? (sidebarRef.current?.getBoundingClientRect().width ?? MIN_EXPANDED_WIDTH)
			: current.width
		const step = KEYBOARD_STEP * (event.shiftKey ? 4 : 1)
		const targets = {
			ArrowLeft: base - step,
			ArrowRight: base + step,
			Home: MIN_EXPANDED_WIDTH,
			End: null // ancho de diseño
		}
		const target = targets[event.key]
		if (target === undefined) return
		event.preventDefault()
		apply(target === null ? { collapsed: false, width: null } : nextStateFromDrag(target, current))
	}, [apply, toggle])

	return { layout, resizing, toggle, onGripPointerDown, onGripKeyDown, sidebarRef }
}

export default useSidebarLayout
